/**
 * The analytics identity — the single projection from the application user
 * onto the property names, value shapes and group identity that the analytics
 * vendor receives.
 *
 * Pure: no framework, no store, no HTTP and no vendor SDK dependencies.
 * `plugins/amplitude.ts` is vendor wiring only and holds no transformation
 * of its own.
 */
import { formatUserName, type UserName } from '@/domain/case/personName';

/** The property keys sent to the vendor. Declared exactly once, here. */
export const NAME_PROPERTY = 'name';
export const EMAIL_PROPERTY = 'email';
export const USER_ROLES_PROPERTY = 'user_roles';
export const ORGANISATION_NAME_PROPERTY = 'organisation_name';
export const ORGANISATION_ID_PROPERTY = 'organisation_id';
export const LAST_ACTIVE_PROPERTY = 'lastActive';

/** The vendor group type: lower case, British spelling, singular. */
export const ORGANISATION_GROUP_TYPE = 'organisation';

/** The subset of user properties that travels on every tracked event. */
export const EVENT_USER_PROPERTY_KEYS: readonly string[] = [
    USER_ROLES_PROPERTY,
    ORGANISATION_NAME_PROPERTY,
    ORGANISATION_ID_PROPERTY,
];

/**
 * `string | string[]`, deliberately not `string | readonly string[]`: the
 * vendor's `Identify.set` takes `Array<string | number>`, to which a readonly
 * array is not assignable. The readonly form would force a widening cast in
 * the plugin — exactly the untestable logic this module removes from it.
 */
export type AnalyticsPropertyValue = string | string[];

export interface AnalyticsGroup {
    readonly groupType: string;
    readonly groupName: string;
}

export interface AnalyticsIdentity {
    /** `undefined` = no identifiable user; the SDK falls back to the device id. */
    readonly userId: string | undefined;
    /** Only keys with a real value are present. Never holds undefined, null or ''. */
    readonly userProperties: Readonly<Record<string, AnalyticsPropertyValue>>;
    /** Empty when the user has no organisation — the plugin then sets no group. */
    readonly groups: readonly AnalyticsGroup[];
}

/**
 * The narrow shape this module needs from the current user.
 *
 * Declared here rather than imported from the API layer, so the domain stays
 * free of `@/api/*`. Every outer key is required with a nullable value type,
 * so renaming a key on the wire type breaks the type check instead of
 * silently emptying the analytics stream.
 */
export interface AnalyticsUserInput {
    readonly id: number | string;
    readonly email: string;
    readonly name: UserName | null;
    readonly roles: ReadonlyArray<{ readonly name: string }>;
    readonly organisation: { readonly id: number | string; readonly name: string } | null;
}

const USER_ID_PAD_TO_LENGTH = 5;
const USER_ID_MAX_LENGTH = 255;

/**
 * Normalise an application user id onto the vendor user id.
 *
 * An empty id yields `undefined`: such a session stays anonymous against the
 * device id instead of merging into one shared phantom user.
 */
export function toAmplitudeUserId(userId: string): string | undefined {
    if (userId.length === 0) {
        return undefined;
    }
    if (userId.length > USER_ID_MAX_LENGTH) {
        return userId.substring(0, USER_ID_MAX_LENGTH);
    }
    return userId.padStart(USER_ID_PAD_TO_LENGTH, '0');
}

/**
 * Project the current user onto the analytics identity.
 *
 * `now` is injected so the result is deterministic. A missing user gives an
 * empty identity rather than a throw.
 */
export function buildAnalyticsIdentity(
    user: AnalyticsUserInput | null | undefined,
    now: Date,
): AnalyticsIdentity {
    if (!user) {
        return { userId: undefined, userProperties: {}, groups: [] };
    }

    const organisationName = usableString(user.organisation?.name);
    const userProperties: Record<string, AnalyticsPropertyValue> = {
        // Always present, so a user who has lost their roles clears the
        // previous value instead of keeping a stale one.
        [USER_ROLES_PROPERTY]: roleNames(user.roles),
        [LAST_ACTIVE_PROPERTY]: now.toISOString(),
    };
    setStringProperty(userProperties, NAME_PROPERTY, formatUserName(user.name));
    setStringProperty(userProperties, EMAIL_PROPERTY, user.email);
    setStringProperty(userProperties, ORGANISATION_NAME_PROPERTY, organisationName);
    setStringProperty(userProperties, ORGANISATION_ID_PROPERTY, organisationId(user.organisation));

    return {
        userId: toAmplitudeUserId(String(user.id)),
        userProperties,
        groups:
            organisationName === undefined
                ? []
                : [{ groupType: ORGANISATION_GROUP_TYPE, groupName: organisationName }],
    };
}

/**
 * The targeting subset carried on tracked events, derived from the identify
 * payload so the two can never drift apart.
 */
export function pickEventUserProperties(
    identity: AnalyticsIdentity,
): Readonly<Record<string, AnalyticsPropertyValue>> {
    const picked: Record<string, AnalyticsPropertyValue> = {};
    EVENT_USER_PROPERTY_KEYS.forEach((key) => {
        const value = identity.userProperties[key];
        if (value !== undefined) {
            picked[key] = value;
        }
    });
    return picked;
}

/**
 * The role names, in source order, with duplicates kept.
 *
 * The wire type states the contract but does not validate it, so entries
 * without a usable name are dropped rather than emitted as holes.
 */
function roleNames(roles: AnalyticsUserInput['roles']): string[] {
    const entries: ReadonlyArray<{ readonly name: string }> = Array.isArray(roles) ? roles : [];
    return entries
        .map((entry) => usableString(entry?.name))
        .filter((name): name is string => name !== undefined);
}

/** The organisation id as a string, or `undefined` when there is no usable id. */
function organisationId(organisation: AnalyticsUserInput['organisation']): string | undefined {
    const id = organisation?.id;
    if (typeof id === 'number') {
        return String(id);
    }
    return usableString(id);
}

/**
 * The value when it carries content, otherwise `undefined`. The value is
 * returned verbatim — the blank test never rewrites what it lets through.
 */
function usableString(value: string | undefined): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

function setStringProperty(
    properties: Record<string, AnalyticsPropertyValue>,
    key: string,
    value: string | undefined,
): void {
    const usable = usableString(value);
    if (usable !== undefined) {
        properties[key] = usable;
    }
}
